import React, {JSX, useEffect, useMemo, useRef, useState} from 'react';
import {t} from 'i18next';
import {Box, Button, Typography} from '@mui/material';
import ArrAccordion from '../../../common/arr-accordion/ArrAccordion';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import {ISelectItem} from '../../../../shared/types';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import './ArrSourceSelectedContainer.scss';
import {
  SourceTypeCode,
  transition_time_default,
} from '../../../../shared/constants';
import ArrSelectionChip from '../../../common/arr-selection-chip/ArrSelectionChip';
import {useLocation} from 'react-router-dom';

export type SelectedSource = {
  id: number;
  sectionId: string;
  sectionLabel: string;
  label: string;
  nodeId: string;
  hideClose?: boolean;
};

export type SourceItem = {
  id: string;
  label: string;
  data: any[];
  [key: string]: any;
};

export interface ArrSourceSelectedContainerProps {
  sources: SourceItem[];
  onSelectionsUpdate: (map: Record<string, any[] | null>) => void;
  preSelectedSource?: string[] | null;
}

const ArrSourceSelectedContainer: React.FC<ArrSourceSelectedContainerProps> = ({
  sources,
  onSelectionsUpdate,
  preSelectedSource,
}) => {
  const location = useLocation();

  const [selectedSources, setSelectedSources] = useState<SelectedSource[]>([]);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [panelOpen, setPanelOpen] = useState<boolean>(true);
  const [selectSourceKey, setSelectSourceKey] = useState<number>(0);

  const [selectedSourceType, setSelectedSourceType] =
    useState<ISelectItem | null>(null);

  const [activeSourceType, setActiveSourceType] = useState<ISelectItem | null>(
    null
  );

  const [committedIdsBySection, setCommittedIdsBySection] = useState<
    Record<string, string[] | null>
  >({});
  const [draftIdsBySection, setDraftIdsBySection] = useState<
    Record<string, string[] | null>
  >({});
  const [, setFullSelectionsBySection] = useState<Record<string, any[] | null>>(
    {}
  );

  const idRef = useRef<number>(1);

  const [allowHeavyCompute, setAllowHeavyCompute] = useState(false);
  const treeCacheRef = useRef<Record<string, any[]>>({});
  const lookupCacheRef = useRef<Record<string, Record<string, any>>>({});

  const [preparedExpandedIds, setPreparedExpandedIds] = useState<
    string[] | null
  >(null);
  const [expandReady, setExpandReady] = useState(false);

  const expandQueueRef = useRef<any[] | null>(null);
  const expandResultRef = useRef<string[]>([]);
  const ricIdRef = useRef<number | null>(null);

  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);

  const unmountTimerRef = useRef<number | null>(null);
  const openSyncIdRef = useRef<number | null>(null);
  const computeDelayRafRef = useRef<number | null>(null);

  const [dropdownAriaLabel, setDropdownAriaLabel] = useState<string>(
    t('reports.tally.sourceContainer.selectSource')
  );

  const [redirectMode, setRedirectMode] = useState<boolean>(false);
  const redirectedAppliedRef = useRef<boolean>(false);

  const collectSectionMatches = (
    sourcesList: SourceItem[],
    ids: string[]
  ): {section: SourceItem; matchedIds: string[]} | null => {
    const idSet = new Set((ids ?? []).map(String));
    let best: {section: SourceItem; matchedIds: string[]} | null = null;

    for (const s of sourcesList) {
      const lookup = buildLookupMap(s?.data ?? []);
      const matches = [...idSet].filter((id) => !!lookup[id]);
      if (matches.length) {
        if (!best || matches.length > best.matchedIds.length) {
          best = {section: s, matchedIds: matches};
        }
      }
    }
    return best;
  };

  const [pendingPreselect, setPendingPreselect] = useState<string[] | null>(
    null
  );

  useEffect(() => {
    if (Array.isArray(preSelectedSource) && preSelectedSource.length) {
      setPendingPreselect(preSelectedSource.map(String));
    }
  }, [preSelectedSource]);

  useEffect(() => {
    if (!pendingPreselect || pendingPreselect.length === 0) return;
    if (!Array.isArray(sources) || sources.length === 0) return;

    const best = collectSectionMatches(sources, pendingPreselect);
    if (!best) {
      setPendingPreselect(null);
      return;
    }

    const {section, matchedIds} = best;
    if (!matchedIds.length) {
      setPendingPreselect(null);
      return;
    }

    const sectionId = String(section.id);
    const sectionLabel =
      section?.chipLabel ||
      section.label ||
      t('ingestions.mapped.tileMenu.source');

    const lookup = buildLookupMap(section?.data ?? []);
    const entries = matchedIds
      .map((id) => {
        const obj = lookup[id];
        const label = pickNodeLabel(obj);
        return label ? {label, nodeId: id} : null;
      })
      .filter(Boolean) as {label: string; nodeId: string}[];

    if (!entries.length) {
      setPendingPreselect(null);
      return;
    }

    const dropdownItem: ISelectItem = {id: section.id, name: section.label};
    setSelectedSourceType(dropdownItem);
    setActiveSourceType(dropdownItem);
    setDropdownAriaLabel(
      `${t('reports.tally.sourceContainer.selectSource')} ${section.label}`
    );

    setCommittedIdsBySection((prev) => ({...prev, [sectionId]: matchedIds}));
    setDraftIdsBySection((prev) => ({...prev, [sectionId]: matchedIds}));
    setFullSelectionsBySection((prev) => ({
      ...prev,
      [sectionId]: matchedIds.map((id) => lookup[id]).filter(Boolean),
    }));

    const isBulkTag =
      !!location?.state &&
      location?.state.navigationSourceType === SourceTypeCode.BULKTAG;

    const chips: SelectedSource[] = entries.map((e) => ({
      id: idRef.current++,
      sectionId,
      sectionLabel,
      label: e.label,
      nodeId: e.nodeId,
    }));

    if (!isBulkTag) {
      setSelectedSources(chips);
    }

    const cleanedIds = matchedIds.map(String).filter((v) => /^-?\d+$/.test(v));
    const isSecGroup = matchedIds.some((id) => {
      const obj = lookup[id];
      return (
        obj && (obj.securityGroupId != null || obj.securityGroupLabel != null)
      );
    });

    const sourceLabel = section.label;

    const navigationSourceType = isSecGroup
      ? 'SECGROUP'
      : sourceLabel.toUpperCase();

    onSelectionsUpdate({
      [sectionId]: [
        {
          sourceName: sourceLabel.toUpperCase(),
          source: cleanedIds,
          navigationSourceType,
          labels: chips
            .map((item) => `${item.sectionLabel}: ${item.label}`)
            .join(', '),
        },
      ],
    });

    setSelectSourceKey((k) => k + 1);
    setPendingPreselect(null);
  }, [pendingPreselect, sources]);

  const openDrawer = () => {
    if (unmountTimerRef.current) {
      cancelAnimationFrame(unmountTimerRef.current);
      unmountTimerRef.current = null;
    }
    if (!drawerOpen) setDrawerOpen(true);
    if (openSyncIdRef.current) cancelAnimationFrame(openSyncIdRef.current);
    openSyncIdRef.current = requestAnimationFrame(() => {
      openSyncIdRef.current = requestAnimationFrame(() => {
        setDrawerVisible(true);
      });
    });
  };

  const closeDrawer = () => {
    const sid = activeSourceType?.id != null ? String(activeSourceType.id) : '';
    if (sid) {
      setDraftIdsBySection((prev) => ({
        ...prev,
        [sid]: committedIdsBySection[sid] ?? null,
      }));
    }
    setDrawerVisible(false);
    setAllowHeavyCompute(false);

    if (computeDelayRafRef.current) {
      cancelAnimationFrame(computeDelayRafRef.current);
      computeDelayRafRef.current = null;
    }

    const start = performance.now();
    const duration = transition_time_default + 80;
    const step = () => {
      const elapsed = performance.now() - start;
      if (elapsed >= duration) {
        setDrawerOpen(false);
        unmountTimerRef.current = null;
      } else {
        unmountTimerRef.current = requestAnimationFrame(step);
      }
    };
    unmountTimerRef.current = requestAnimationFrame(step);
    setActiveSourceType(null);
  };

  useEffect(() => {
    setPanelOpen(!drawerVisible);
  }, [drawerVisible]);

  useEffect(() => {
    treeCacheRef.current = {};
    lookupCacheRef.current = {};
  }, [sources]);

  useEffect(() => {
    const state: any = location?.state as any;

    const hasCommMap =
      !!state &&
      state.navigationSourceType === 'DOCLIST' &&
      state.sourceName === 'COMMUNICATIONMAP' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasConceptExplorer =
      !!state &&
      state.navigationSourceType === 'CONCEPTEXPLORER' &&
      state.sourceName === 'DOCLIST' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasTimeline =
      !!state &&
      state.navigationSourceType === 'TIMELINE' &&
      state.sourceName === 'DOCLIST' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasIngestion =
      !!state &&
      state.navigationSourceType === 'INGESTION' &&
      state.sourceName === 'DOCLIST' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasAdvBatchMgt =
      !!state &&
      state.navigationSourceType === 'ADVBATMGMTRPT' &&
      state.sourceName === 'DOCLIST' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasSessionSearch =
      !!state &&
      state.navigationSourceType === 'SESSIONSEARCH' &&
      state.sourceName === 'DOCLIST' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasSavedSearch =
      !!state &&
      state.navigationSourceType === 'SEARCH' &&
      state.sourceName === 'SEARCHES' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasDocList =
      !!state &&
      state.navigationSourceType === 'DOCLIST' &&
      state.sourceName === 'DOCLIST' &&
      Array.isArray(state.source) &&
      state.source.length > 0;
    const hasBulkTag =
      !!state &&
      state.navigationSourceType === SourceTypeCode.BULKTAG &&
      state.sourceName === SourceTypeCode.BULKTAG &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    if (
      hasCommMap ||
      hasConceptExplorer ||
      hasTimeline ||
      hasIngestion ||
      hasAdvBatchMgt ||
      hasSessionSearch ||
      hasSavedSearch ||
      hasDocList ||
      hasBulkTag
    ) {
      setRedirectMode(true);

      if (!redirectedAppliedRef.current) {
        redirectedAppliedRef.current = true;

        const labelText = hasCommMap
          ? 'Selected Documents from Communication Map'
          : hasConceptExplorer
            ? 'Selected Documents from Concept Explorer'
            : hasTimeline
              ? 'Selected Documents from Timeline'
              : hasIngestion
                ? 'Selected Documents from Ingestion'
                : hasAdvBatchMgt
                  ? 'Selected Documents from AdvBatchMangementRpt'
                  : hasSessionSearch
                    ? 'Selected Documents from Search'
                    : hasSavedSearch
                      ? `Selected Documents from SavedSearch: ${state.savedSearchName ?? ''}`
                      : hasBulkTag
                        ? t('reports.tally.sourceContainer.bulkTagAction')
                        : 'Selected Documents from Doc List';

        const sectionId = hasSavedSearch ? 'searches' : 'documents';
        const sectionLabel = hasSavedSearch ? 'Search' : 'Documents';

        const chip = {
          id: idRef.current++,
          sectionId,
          sectionLabel,
          label: labelText,
          nodeId: String(state.source?.[0] ?? ''),
        };

        setSelectedSources([chip]);

        setCommittedIdsBySection(() => ({
          [sectionId]: state.source.map(String),
        }));
        setDraftIdsBySection(() => ({
          [sectionId]: state.source.map(String),
        }));
        setFullSelectionsBySection(() => ({[sectionId]: null}));

        onSelectionsUpdate({
          [sectionId]: [
            {
              sourceName: state.sourceName,
              source: state.source,
              navigationSourceType: state.navigationSourceType,
              labels: `${sectionLabel}: ${labelText}`,
            },
          ],
        });

        setSelectSourceKey((k) => k + 1);
      }
    }
  }, [location?.state, onSelectionsUpdate]);

  useEffect(() => {
    if (drawerVisible) {
      setAllowHeavyCompute(false);
      const minDelay = Math.max(
        180,
        Math.floor(transition_time_default * 0.65)
      );
      const start = performance.now();
      const tick = () => {
        if (!drawerVisible) return;
        const elapsed = performance.now() - start;
        if (elapsed >= minDelay) {
          setAllowHeavyCompute(true);
          computeDelayRafRef.current = null;
        } else {
          computeDelayRafRef.current = requestAnimationFrame(tick);
        }
      };
      computeDelayRafRef.current = requestAnimationFrame(tick);
      return () => {
        if (computeDelayRafRef.current) {
          cancelAnimationFrame(computeDelayRafRef.current);
          computeDelayRafRef.current = null;
        }
      };
    } else {
      setAllowHeavyCompute(false);
      setPreparedExpandedIds(null);
      setExpandReady(false);
      expandQueueRef.current = null;
      expandResultRef.current = [];
      if (ricIdRef.current != null) {
        const w: any = window as any;
        if (w.cancelIdleCallback) w.cancelIdleCallback(ricIdRef.current);
        else cancelAnimationFrame(ricIdRef.current);
        ricIdRef.current = null;
      }
    }
  }, [drawerVisible]);

  const sourceOptions: ISelectItem[] = sources
    .filter((s) => !(Array.isArray(s.data) && s.data.length === 0))
    .map((s) => ({id: s.id, name: s.label}));

  const pickNodeLabel = (node: any) =>
    node?.text ||
    node?.securityGroupLabel ||
    node?.projectLabel ||
    node?.label ||
    node?.name ||
    '';

  const getNormalizedId = (n: any): string =>
    String(
      n?.id ??
        n?.value ??
        n?.securityGroupId ??
        n?.projectId ??
        n?.tagClassificationId ??
        ''
    );

  const buildLookupMap = (flatList: any[] = []): Record<string, any> => {
    const map: Record<string, any> = {};
    (flatList ?? []).forEach((n) => {
      const id = getNormalizedId(n);
      if (id) map[id] = n;
    });
    return map;
  };

  const buildNestedTree = (flatList: any[] = []): any[] => {
    const nodeMap: Record<string, any> = {};
    const roots: any[] = [];
    (flatList ?? []).forEach((node) => {
      const id = getNormalizedId(node);
      const parentId = node.parent != null ? String(node.parent) : '';
      nodeMap[id] = {...node, id, parentId, children: []};
    });
    Object.values(nodeMap).forEach((node: any) => {
      if (node.parentId && nodeMap[node.parentId]) {
        nodeMap[node.parentId].children.push(node);
      } else {
        roots.push(node);
      }
    });
    return roots;
  };

  const currentSectionId =
    activeSourceType?.id != null ? String(activeSourceType.id) : '';

  const mapToTreeItems = (nodes: any[] = []): any[] =>
    (nodes ?? []).map((n: any) => {
      const id = getNormalizedId(n);
      const label = pickNodeLabel(n);
      const children = Array.isArray(n.children)
        ? mapToTreeItems(n.children)
        : undefined;

      const disableEmptySearchGroups =
        currentSectionId.toLowerCase() === 'searches' &&
        (label === 'Shared With Project Administrator' ||
          label === 'My Saved Search') &&
        (!children || children.length === 0);

      return {
        id,
        label,
        fileType:
          children && children.length
            ? `${t('reports.tally.sourceContainer.folder')}`
            : `${t('reports.tally.sourceContainer.search')}`,
        disabled: !!n.disabled || disableEmptySearchGroups,
        count: n.count,
        children,
      };
    });

  const currentSection = useMemo<SourceItem | null>(() => {
    return currentSectionId
      ? (sources.find((s) => s.id === currentSectionId) ?? null)
      : null;
  }, [sources, currentSectionId]);

  const currentLookup = useMemo(() => {
    const sid = currentSection?.id ?? '';
    if (!sid) return {};
    if (!allowHeavyCompute) return lookupCacheRef.current[sid] ?? {};
    if (!lookupCacheRef.current[sid]) {
      lookupCacheRef.current[sid] = buildLookupMap(currentSection?.data ?? []);
    }
    return lookupCacheRef.current[sid];
  }, [currentSection, allowHeavyCompute]);

  const treeItems = useMemo(() => {
    const sid = currentSection?.id ?? '';
    if (!sid) return [];
    if (!allowHeavyCompute) return treeCacheRef.current[sid] ?? [];
    if (!treeCacheRef.current[sid]) {
      const rawData = currentSection?.data ?? [];
      const nestedData = buildNestedTree(rawData);
      treeCacheRef.current[sid] = mapToTreeItems(nestedData);
    }
    return treeCacheRef.current[sid];
  }, [currentSection, allowHeavyCompute]);

  const rootFolderIds = useMemo(() => {
    if (!allowHeavyCompute) return [];
    return (treeItems ?? [])
      .filter((n: any) => n.children && n.children.length)
      .map((n: any) => n.id);
  }, [allowHeavyCompute, treeItems]);

  useEffect(() => {
    if (!drawerVisible || !allowHeavyCompute) return;

    setPreparedExpandedIds(null);
    setExpandReady(false);
    expandResultRef.current = [];
    expandQueueRef.current = [...(treeItems ?? [])];

    const run = (deadline: {timeRemaining: () => number}) => {
      if (!expandQueueRef.current) return;
      let processed = 0;

      while (
        expandQueueRef.current.length &&
        (deadline.timeRemaining() > 1 || processed < 120)
      ) {
        const node = expandQueueRef.current.shift();
        if (node && node.children && node.children.length) {
          expandResultRef.current.push(node.id);
          for (let i = 0; i < node.children.length; i++) {
            expandQueueRef.current.push(node.children[i]);
          }
        }
        processed++;
        if (processed >= 4000) break;
      }

      if (expandQueueRef.current.length) {
        const w: any = window as any;
        if (w.requestIdleCallback) {
          ricIdRef.current = w.requestIdleCallback(run);
        } else {
          ricIdRef.current = requestAnimationFrame(() =>
            run({timeRemaining: () => 0})
          );
        }
      } else {
        setPreparedExpandedIds(expandResultRef.current.slice());
        setExpandReady(true);
        setSelectSourceKey((k) => k + 1);
        expandQueueRef.current = null;
        expandResultRef.current = [];
        ricIdRef.current = null;
      }
    };

    const w: any = window as any;
    if (w.requestIdleCallback) {
      ricIdRef.current = w.requestIdleCallback(run);
    } else {
      ricIdRef.current = requestAnimationFrame(() =>
        run({timeRemaining: () => 0})
      );
    }

    return () => {
      if (ricIdRef.current != null) {
        if (w.cancelIdleCallback) w.cancelIdleCallback(ricIdRef.current);
        else cancelAnimationFrame(ricIdRef.current);
        ricIdRef.current = null;
      }
    };
  }, [drawerVisible, allowHeavyCompute, treeItems]);

  const defaultExpandedIds = useMemo(() => {
    return undefined as unknown as string[] | undefined;
  }, [allowHeavyCompute, expandReady, preparedExpandedIds, rootFolderIds]);

  const handleDraftIdsChangeForSection = (
    sectionId: string,
    ids: string[] | null
  ) => {
    setDraftIdsBySection((prev) => ({...prev, [sectionId]: ids}));
  };

  const handleSaveSelection = (
    sectionLabel: string,
    sectionId: string,
    sectionChipLabel: string,
    ids: string[] | null,
    lookup: Record<string, any>
  ) => {
    if (!ids || !ids.length) {
      closeDrawer();
      return;
    }

    const entries = ids
      .map((id) => {
        const obj = lookup[id];
        const label = pickNodeLabel(obj);
        return label ? {label, nodeId: id} : null;
      })
      .filter(Boolean) as {label: string; nodeId: string}[];

    if (!entries.length) {
      closeDrawer();
      return;
    }

    const sourceSelected = entries.map((e) => ({
      id: idRef.current++,
      sectionId,
      sectionLabel: sectionChipLabel,
      label: e.label,
      nodeId: e.nodeId,
    }));

    setSelectedSources(sourceSelected);
    setCommittedIdsBySection(() => ({[sectionId]: ids}));
    setDraftIdsBySection(() => ({[sectionId]: ids}));
    setFullSelectionsBySection(() => ({
      [sectionId]: ids.map((id) => lookup[id]).filter(Boolean),
    }));

    const cleanedIds = (ids ?? []).map(String).filter((v) => /^-?\d+$/.test(v));
    const isSecGroup = (ids ?? []).some((id) => {
      const obj = lookup[id];
      return (
        obj && (obj.securityGroupId != null || obj.securityGroupLabel != null)
      );
    });

    const navigationSourceType = isSecGroup
      ? 'SECGROUP'
      : sectionLabel.toUpperCase();

    onSelectionsUpdate({
      [sectionId]: [
        {
          sourceName: sectionLabel.toUpperCase(),
          source: cleanedIds,
          navigationSourceType,
          labels: sourceSelected
            .map((item) => `${item.sectionLabel}: ${item.label}`)
            .join(', '),
        },
      ],
    });

    if (activeSourceType) {
      const selected = sources.find(
        (s) => s.id === String(activeSourceType.id)
      );
      if (selected) {
        setSelectedSourceType(activeSourceType);
        setDropdownAriaLabel(
          `${t('reports.tally.sourceContainer.selectSource')} ${selected.label}`
        );
      }
    }

    setSelectSourceKey((k) => k + 1);
    closeDrawer();
  };

  const handleRemoveChip = (source: SelectedSource) => {
    setSelectedSources((prev) => {
      const next = prev.filter((i) => i.id !== source.id);

      if (next.length === 0) {
        onSelectionsUpdate({});
        setSelectedSourceType(null);
        setDropdownAriaLabel(t('reports.tally.sourceContainer.selectSource'));
        setCommittedIdsBySection({});
        setDraftIdsBySection({});
        setFullSelectionsBySection({});
        setActiveSourceType(null);
        setSelectSourceKey((k) => k + 1);
        setRedirectMode(false);
        redirectedAppliedRef.current = false;
      }
      return next;
    });
    setSelectSourceKey((k) => k + 1);
  };

  const content: JSX.Element = useMemo(
    () => (
      <Box
        className="arr-chips-container"
        data-testid="arr-chips-container"
        sx={{
          alignItems: selectedSources.length > 0 ? 'flex-start' : 'center',
          justifyContent: selectedSources.length > 0 ? 'flex-start' : 'center',
        }}
        role="region"
        aria-label={
          selectedSources.length > 0
            ? t('reports.tally.sourceContainer.selectSources')
            : t('reports.tally.sourceContainer.noSourceMessage')
        }
      >
        {selectedSources.length > 0 ? (
          selectedSources.map((source) => (
            <ArrSelectionChip
              key={`${source.sectionId}-${source.id}`}
              filterData={{
                id: `${source.sectionId}${source.id}`,
                label: `${source.sectionLabel}: ${source.label}`,
                isSelected: false,
              }}
              onClick={() => handleRemoveChip(source)}
              dataTestId={source.sectionLabel
                ?.replaceAll(' ', '')
                .toLowerCase()}
              variant="v2"
              showCloseIcon={true}
              aria-label={`${source.sectionLabel} ${source.label}`}
              labelTextTestId={source.sectionId}
            />
          ))
        ) : (
          <div
            className="no-source-container"
            data-testid={`arr-source-selected-container-no-source-message`}
          >
            <Typography
              variant="body2"
              data-testid="arr-no-selection-message"
              className="unavailable-text"
              aria-label={t('reports.tally.sourceContainer.noSourceMessage')}
            >
              {t('reports.tally.sourceContainer.noSourceMessage')}
            </Typography>
          </div>
        )}
      </Box>
    ),
    [selectedSources]
  );

  const ACC_LABEL_ID = 'arr-accordion-title-label';

  const accordionSections = [
    {
      id: 'sourceContainer',
      title: (
        <div
          className="arr-accordion-title-container"
          data-testid="arr-accordion-title"
          onClick={(e) => e.stopPropagation()}
        >
          <Typography
            className="accordion-title-label"
            data-testid="arr-accordion-title-label"
            id={ACC_LABEL_ID}
            aria-label={t('reports.tally.sourceContainer.selectSources')}
            tabIndex={0}
          >
            {t('reports.tally.sourceContainer.selectSources')}
          </Typography>

          <div
            style={{
              pointerEvents: redirectMode ? 'none' : 'auto',
              opacity: redirectMode ? 0.6 : 1,
              filter: redirectMode ? 'grayscale(1) blur(0.4px)' : 'none',
            }}
            data-testid={`arr-source-selected-container-source-container`}
          >
            <ArrSingleSelectDropdown
              key={selectSourceKey}
              dropdownLabel={t('reports.tally.sourceContainer.selectSource')}
              dropdownItems={sourceOptions}
              selectedValues={selectedSourceType}
              disabled={redirectMode}
              onSelectionChange={(item: ISelectItem | null) => {
                if (redirectMode) return;
                const selected = sources.find((s) => s.id === String(item?.id));
                if (selected && item) {
                  const sid = String(item.id);
                  setDraftIdsBySection((prev) => ({
                    ...prev,
                    [sid]: committedIdsBySection[sid] ?? null,
                  }));
                  setActiveSourceType(item);
                  openDrawer();
                  setSelectSourceKey((k) => k + 1);
                } else {
                  setDropdownAriaLabel(
                    t('reports.tally.sourceContainer.selectSource')
                  );
                }
              }}
              sizeVariant="medium"
              customWidth={20}
              dataTestId="asd-arr-source-selected-container"
              aria-label={dropdownAriaLabel}
              aria-labelledby={ACC_LABEL_ID}
            />
          </div>
        </div>
      ),
      content: content,
    },
  ];

  const headerTitle = (
    <Box className="flexCenter">
      <Box className="flexBoxWhite">
        <Typography
          variant="caption"
          className="captionBlock"
          aria-label={t('ingestions.mapped.tileMenu.source')}
          data-testid={`arr-source-selected-container-source`}
        >
          {t('ingestions.mapped.tileMenu.source')}
        </Typography>
        <Typography
          variant="h6"
          className="typographyEllipsis"
          aria-label={currentSection?.label}
          data-testid={`arr-source-selected-container-label`}
        >
          {currentSection?.label}
        </Typography>
      </Box>

      <Box className="button-container">
        <Button
          variant="contained"
          className="cancel-btn"
          onClick={() => closeDrawer()}
          data-testid={`arr-cancel-button-${currentSectionId}`}
          aria-label={t('common.cancel')}
        >
          <Typography
            className="cancel-button-text"
            aria-label={t('common.cancel')}
            data-testid={`arr-source-selected-container-cancel`}
          >
            {t('common.cancel')}
          </Typography>
        </Button>

        <Button
          variant="contained"
          className={`select-btn ${
            !Array.isArray(draftIdsBySection[currentSectionId]) ||
            (draftIdsBySection[currentSectionId] ?? []).length === 0
              ? 'inactive'
              : ''
          }`}
          onClick={() =>
            handleSaveSelection(
              currentSection?.label ?? t('ingestions.mapped.tileMenu.source'),
              currentSectionId,
              currentSection?.chipLabel ||
                currentSection?.label ||
                t('ingestions.mapped.tileMenu.source'),
              Array.isArray(draftIdsBySection[currentSectionId])
                ? draftIdsBySection[currentSectionId]
                : null,
              currentLookup
            )
          }
          disabled={
            !Array.isArray(draftIdsBySection[currentSectionId]) ||
            (draftIdsBySection[currentSectionId] ?? []).length === 0
          }
          data-testid={`arr-save-button-${currentSectionId}`}
          aria-label={t('common.select')}
        >
          <Typography
            className={`select-button-text ${
              !Array.isArray(draftIdsBySection[currentSectionId]) ||
              (draftIdsBySection[currentSectionId] ?? []).length === 0
                ? 'inactive'
                : ''
            }`}
            aria-label={t('common.select')}
            data-testid={`arr-source-selected-container-select`}
          >
            {t('common.select')}
          </Typography>
        </Button>
      </Box>
    </Box>
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderDrawer = (isOpen: boolean): JSX.Element => (
    <ArrDrawer
      transition={transition_time_default}
      isOpen={drawerVisible}
      onClose={() => {}}
      title={headerTitle as unknown as string}
      showButton={false}
      size="small"
      side="right"
      trapFocus={true}
      data-testid="arr-source-drawer"
      aria-label={t('reports.tally.sourceContainer.selectSources')}
    >
      <Box
        className="drawerContent"
        data-testid="arr-drawer-content"
        sx={{height: '100%'}}
        role="region"
        aria-label={t('reports.tally.sourceContainer.selectSources')}
      >
        <Box
          className="arr-tree-container"
          data-testid={`arr-tree-container-${currentSectionId}`}
          sx={{height: '100%', overflowY: 'auto'}}
          role="region"
          aria-label={t('reports.tally.sourceContainer.selectSources')}
        >
          <ArrTreeView
            key={selectSourceKey + (expandReady ? 1 : 0)}
            itemsList={treeItems}
            isShowCheckbox={true}
            multipleSelection={true}
            treeSelectedItems={
              Array.isArray(draftIdsBySection[currentSectionId])
                ? draftIdsBySection[currentSectionId]
                : null
            }
            setSelectedItem={(val: any) => {
              const ids = Array.isArray(val)
                ? val.map(String)
                : val
                  ? [String(val)]
                  : [];
              handleDraftIdsChangeForSection(
                currentSectionId,
                ids.length ? ids : null
              );
            }}
            defaultExpandedIds={defaultExpandedIds as unknown as string[]}
            data-testid={`arr-treeview-${currentSectionId}`}
            isShowFileType={false}
            isChildrenIndendation={true}
            aria-label={
              currentSection?.label ??
              t('reports.tally.sourceContainer.selectSources')
            }
          />
        </Box>
      </Box>
    </ArrDrawer>
  );

  return (
    <>
      <ArrAccordion
        sections={accordionSections}
        expandedSection={panelOpen ? 'sourceContainer' : undefined}
        onToggle={(id: string) => setPanelOpen(id === 'sourceContainer')}
        dataTestId="selected-source"
      />
      {drawerOpen && renderDrawer(drawerOpen)}
    </>
  );
};

export default ArrSourceSelectedContainer;
